// Survey Management ("Survey Capture") — create and manage feedback capture
// instruments. This is NOT a data-volume / reporting page.
// Mounted at /console/surveys. Localized via the `surveyManagement` i18n namespace.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getIntlLocale } from "util/locale";
import {
  Button,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  message,
} from "antd";
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  StopOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  STATUS_FILTERS,
  STATUS_META,
  SURVEY_STATUS,
} from "./constants";
import {
  createSurvey,
  deleteSurvey,
  duplicateSurvey,
  listSurveys,
  videoAnswersEnabled,
  setSurveyStatus,
  updateSurvey,
} from "./surveyService";
import { useSelector } from "react-redux";
import SurveyBuilderDrawer from "./SurveyBuilderDrawer";
import SurveyPreview from "./SurveyPreview";
import SurveyUpgradeModal from "./SurveyUpgradeModal";
import useSurveyCheckoutReturn from "hooks/useSurveyCheckoutReturn";
import {
  hasSurveyPaidPlan,
  hasSurveyProPlan,
  hasSurveyProPlusPlan,
} from "util/entitlements";
import styles from "./style.module.scss";

const SurveyManagement = ({ user }) => {
  const { t, i18n } = useTranslation();
  const subscriptionStatus = useSelector((state) => state.userReducer);
  const [loading, setLoading] = useState(true);
  const [surveys, setSurveys] = useState([]);
  const [activeStatus, setActiveStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [rowBusyId, setRowBusyId] = useState(null);
  const [previewSurvey, setPreviewSurvey] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeMode, setUpgradeMode] = useState("plus");

  const fetchSurveys = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listSurveys();
      setSurveys(data);
    } catch (e) {
      message.error(t("surveyManagement.msg_load_error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useSurveyCheckoutReturn({ user, onRefresh: fetchSurveys });

  useEffect(() => {
    fetchSurveys();
  }, [fetchSurveys]);

  const counts = useMemo(() => {
    const acc = { all: surveys.length };
    surveys.forEach((s) => {
      acc[s.status] = (acc[s.status] || 0) + 1;
    });
    return acc;
  }, [surveys]);

  const filteredSurveys = useMemo(() => {
    const term = search.trim().toLowerCase();
    return surveys.filter((s) => {
      const matchStatus =
        activeStatus === "all" || s.status === activeStatus;
      const matchSearch =
        !term ||
        s.name.toLowerCase().includes(term) ||
        (s.description || "").toLowerCase().includes(term);
      return matchStatus && matchSearch;
    });
  }, [surveys, activeStatus, search]);

  const openCreate = () => {
    setEditingSurvey(null);
    setDrawerOpen(true);
  };

  const openEdit = (survey) => {
    setEditingSurvey(survey);
    setDrawerOpen(true);
  };

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (editingSurvey) {
        const updated = await updateSurvey(editingSurvey.id, payload);
        setSurveys((prev) =>
          prev.map((s) => (s.id === updated.id ? updated : s))
        );
        message.success(t("surveyManagement.msg_updated"));
      } else {
        const created = await createSurvey(payload);
        setSurveys((prev) => [created, ...prev]);
        message.success(t("surveyManagement.msg_created"));
      }
      setDrawerOpen(false);
      setEditingSurvey(null);
    } catch (e) {
      message.error(t("surveyManagement.msg_save_error"));
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async (survey) => {
    setRowBusyId(survey.id);
    try {
      const copy = await duplicateSurvey(survey.id);
      setSurveys((prev) => [copy, ...prev]);
      message.success(t("surveyManagement.msg_duplicated"));
    } catch (e) {
      message.error(t("surveyManagement.msg_dup_error"));
    } finally {
      setRowBusyId(null);
    }
  };

  const handleToggleStatus = async (survey) => {
    const nextStatus =
      survey.status === SURVEY_STATUS.ACTIVE
        ? SURVEY_STATUS.CLOSED
        : SURVEY_STATUS.ACTIVE;
    setRowBusyId(survey.id);
    try {
      const updated = await setSurveyStatus(survey.id, nextStatus);
      setSurveys((prev) =>
        prev.map((s) => (s.id === updated.id ? updated : s))
      );
      message.success(
        nextStatus === SURVEY_STATUS.ACTIVE
          ? t("surveyManagement.msg_activated")
          : t("surveyManagement.msg_closed")
      );
    } catch (e) {
      message.error(t("surveyManagement.msg_status_error"));
    } finally {
      setRowBusyId(null);
    }
  };

  const handleDelete = async (survey) => {
    setRowBusyId(survey.id);
    try {
      await deleteSurvey(survey.id);
      setSurveys((prev) => prev.filter((s) => s.id !== survey.id));
      message.success(t("surveyManagement.msg_deleted"));
    } catch (e) {
      message.error(t("surveyManagement.msg_delete_error"));
    } finally {
      setRowBusyId(null);
    }
  };

  const columns = [
    {
      title: t("surveyManagement.col_survey"),
      dataIndex: "name",
      key: "name",
      render: (_, survey) => (
        <div className={styles.nameCell}>
          <button
            type="button"
            className={styles.nameLink}
            onClick={() => openEdit(survey)}
          >
            {survey.name}
          </button>
          {survey.description ? (
            <span className={styles.nameDesc} title={survey.description}>
              {survey.description}
            </span>
          ) : null}
          <span className={styles.questionMeta}>
            {survey.questions.length}{" "}
            {survey.questions.length === 1
              ? t("surveyManagement.question_one")
              : t("surveyManagement.question_other")}
          </span>
        </div>
      ),
    },
    {
      title: t("surveyManagement.col_status"),
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (status) => {
        const meta = STATUS_META[status] || STATUS_META[SURVEY_STATUS.DRAFT];
        return <Tag color={meta.color}>{t(meta.labelKey)}</Tag>;
      },
    },
    {
      title: t("surveyManagement.col_responses"),
      dataIndex: "responseCount",
      key: "responseCount",
      width: 120,
      align: "right",
      render: (count) =>
        new Intl.NumberFormat(getIntlLocale(i18n.language)).format(count || 0),
    },
    {
      title: t("surveyManagement.col_created"),
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (createdAt) =>
        createdAt ? dayjs(createdAt).format("D MMM YYYY") : "—",
    },
    {
      title: t("surveyManagement.col_actions"),
      key: "actions",
      width: 240,
      render: (_, survey) => {
        const isActive = survey.status === SURVEY_STATUS.ACTIVE;
        const busy = rowBusyId === survey.id;
        const previewDisplayMode =
          survey.distribution?.web_embed?.enabled &&
          survey.distribution.web_embed.displayMode
            ? survey.distribution.web_embed.displayMode
            : "popup";
        return (
          <div className={styles.rowActions}>
            <Tooltip title={t("surveyManagement.action_preview")}>
              <Button
                size="small"
                type="text"
                icon={<EyeOutlined />}
                onClick={() => setPreviewSurvey({ survey, previewDisplayMode })}
              />
            </Tooltip>
            <Tooltip title={t("surveyManagement.action_edit")}>
              <Button
                size="small"
                type="text"
                icon={<EditOutlined />}
                onClick={() => openEdit(survey)}
              />
            </Tooltip>
            <Tooltip title={t("surveyManagement.action_duplicate")}>
              <Button
                size="small"
                type="text"
                icon={<CopyOutlined />}
                loading={busy}
                onClick={() => handleDuplicate(survey)}
              />
            </Tooltip>
            <Tooltip
              title={
                isActive
                  ? t("surveyManagement.action_close")
                  : t("surveyManagement.action_activate")
              }
            >
              <Button
                size="small"
                type="text"
                icon={isActive ? <StopOutlined /> : <PlayCircleOutlined />}
                loading={busy}
                onClick={() => handleToggleStatus(survey)}
              />
            </Tooltip>
            <Popconfirm
              title={t("surveyManagement.delete_confirm_title")}
              description={t("surveyManagement.delete_confirm_desc")}
              okText={t("surveyManagement.delete")}
              cancelText={t("surveyManagement.cancel")}
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDelete(survey)}
            >
              <Tooltip title={t("surveyManagement.action_delete")}>
                <Button
                  size="small"
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  const tabItems = STATUS_FILTERS.map((f) => ({
    key: f.key,
    label: `${t(f.labelKey)} (${counts[f.key] || 0})`,
  }));

  const showEmptyState = !loading && surveys.length === 0;

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>{t("surveyManagement.title")}</h1>
          <p className={styles.pageSubtitle}>
            {t("surveyManagement.subtitle")}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {!hasSurveyProPlusPlan(subscriptionStatus) ? (
            <Button
              onClick={() => {
                const next = hasSurveyProPlan(subscriptionStatus)
                  ? "pro_plus"
                  : hasSurveyPaidPlan(subscriptionStatus)
                    ? "pro"
                    : "plus";
                setUpgradeMode(next);
                setUpgradeOpen(true);
              }}
            >
              {t("surveyManagement.upgrade_plan_cta")}
            </Button>
          ) : null}
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            {t("surveyManagement.create_survey")}
          </Button>
        </div>
      </div>

      {showEmptyState ? (
        <div className={styles.emptyWrap}>
          <Empty
            description={
              <div className={styles.emptyText}>
                <h3>{t("surveyManagement.empty_title")}</h3>
                <p>{t("surveyManagement.empty_desc")}</p>
              </div>
            }
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              {t("surveyManagement.create_survey")}
            </Button>
          </Empty>
        </div>
      ) : (
        <div className={styles.contentPanel}>
          <div className={styles.toolbar}>
            <Tabs
              activeKey={activeStatus}
              onChange={setActiveStatus}
              items={tabItems}
              className={styles.statusTabs}
            />
            <div className={styles.toolbarRight}>
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder={t("surveyManagement.search_placeholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ width: 220 }}
              />
              <Tooltip title={t("surveyManagement.refresh")}>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={fetchSurveys}
                  loading={loading}
                />
              </Tooltip>
            </div>
          </div>

          <Spin spinning={loading}>
            <Table
              rowKey="id"
              columns={columns}
              dataSource={filteredSurveys}
              pagination={{ pageSize: 10, hideOnSinglePage: true }}
              locale={{
                emptyText: (
                  <div className={styles.tableEmpty}>
                    {t("surveyManagement.empty_no_match")}
                  </div>
                ),
              }}
              className={styles.surveyTable}
            />
          </Spin>
        </div>
      )}

      <SurveyBuilderDrawer
        open={drawerOpen}
        survey={editingSurvey}
        saving={saving}
        onClose={() => {
          setDrawerOpen(false);
          setEditingSurvey(null);
        }}
        onSave={handleSave}
        onUpgrade={(tier) => {
          setUpgradeMode(tier || "plus");
          setUpgradeOpen(true);
        }}
        videoEnabled={videoAnswersEnabled()}
      />

      <SurveyUpgradeModal
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        mode={upgradeMode}
        returnPath="/console/surveys"
        user={user}
        onSuccess={fetchSurveys}
      />

      <Modal
        title={previewSurvey?.survey?.name || t("surveyManagement.preview_title")}
        open={!!previewSurvey}
        onCancel={() => setPreviewSurvey(null)}
        footer={[
          <Button key="close" onClick={() => setPreviewSurvey(null)}>
            {t("surveyManagement.cancel")}
          </Button>,
        ]}
        width={520}
        destroyOnClose
      >
        {previewSurvey ? (
          <div className={styles.previewModalBody}>
            <SurveyPreview
              name={previewSurvey.survey.name}
              description={previewSurvey.survey.description}
              questions={previewSurvey.survey.questions || []}
              displayMode={previewSurvey.previewDisplayMode}
              widgetHeaderTitle={previewSurvey.survey.widgetHeaderTitle || ""}
              showBranding={previewSurvey.survey.showBranding !== false}
              requestContactDetails={previewSurvey.survey.requestContactDetails === true}
              contactDetailsRequired={previewSurvey.survey.contactDetailsRequired === true}
              primaryColor={previewSurvey.survey.primaryColor}
              logoUrl={previewSurvey.survey.logoUrl}
              popupPosition={previewSurvey.survey.popupPosition}
            />
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default SurveyManagement;
